import React from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';

import { SxProps, Theme } from '@mui/material/styles';

export type UsageMeterTone = 'ok' | 'warn' | 'crit';

interface UsageMeterProps {
  used: number;
  /** null = unlimited: the bar is hidden and only the label renders. */
  limit: number | null;
  tone?: UsageMeterTone;
  /** Rendered left/right under the bar. */
  leftLabel?: React.ReactNode;
  rightLabel?: React.ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
  /** Bar height class or numeric value */
  heightClass?: string;
}

const TONE_COLORS: Record<UsageMeterTone, string> = {
  ok: '#10b981',
  warn: '#f59e0b',
  crit: '#f43f5e',
};

export function usagePercent(used: number, limit: number | null): number {
  if (limit === null) return 0;
  if (limit <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

/** "X of Y" bar shared by the upload widget, the bank stats bar, and the license cards. */
export const UsageMeter: React.FC<UsageMeterProps> = ({
  used,
  limit,
  tone = 'ok',
  leftLabel,
  rightLabel,
  className = '',
  sx,
  heightClass,
}) => {
  const percent = usagePercent(used, limit);
  const unlimited = limit === null;
  const barHeight = 8;

  return (
    <Box className={className} data-testid="usage-meter" data-tone={tone} sx={{ width: '100%', ...sx }}>
      {!unlimited && (
        <LinearProgress
          variant="determinate"
          value={percent}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={Math.min(used, limit)}
          sx={{
            height: barHeight,
            borderRadius: '9999px',
            bgcolor: 'rgba(0, 0, 0, 0.08)',
            '& .MuiLinearProgress-bar': {
              borderRadius: '9999px',
              bgcolor: TONE_COLORS[tone],
              transition: 'transform 0.3s ease',
            },
          }}
        />
      )}
      {(leftLabel || rightLabel) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 0.75,
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'text.secondary',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            {leftLabel}
          </Typography>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            {rightLabel}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
